import {
    Conversation,
    ConversationGroup,
    ConversationOwner,
    ConversationStatus,
} from "frontend-lib/DomainTypes/Conversation";
import { Moment } from "moment";
import CTA from "../../Constants";
import { Agent } from "frontend-lib/DomainTypes/User";
import { api } from "Requests/Api";
import { AxiosResponse } from "axios";
import { removeDuplicates } from "Components/ChatHandler/ChatUtils/array";

type ChatSortableFields =
    | "CREATED_ON"
    | "UPDATED_ON"
    | "LAST_END_USER_ACTIVITY"
    | "START_WAITING_ON"
    | "CLOSED_ON"
    | "ASSIGNED_TO_HUMAN_AGENT_ON";

type CustomerChannel =
    | "OPTIWE_CHAT"
    | "WHATSAPP"
    | "INSTAGRAM"
    | "FACEBOOK"
    | "SMS"
    | "FACEBOOK_COMMENT";

export type ChatFilters = {
    status?: ConversationStatus;
    owner: ConversationOwner;
    startDatetime: Moment | null;
    endDatetime: Moment | null;
    dateFieldFilter: ChatSortableFields | null;
    sortBy: ChatSortableFields | null;
    searchText: string | null;
    isWaiting: boolean | null;
    notServed: boolean | null;
    useSortConfig: boolean;
    orderDirection: "ASC" | "DESC" | null;
    filterByUserGroups: boolean | null;
    chatGroupsId: number[] | null;
    conversationLabels: number[] | null;
    participatingAgentsId: number[] | null;
    onlyOwners: boolean | null;
    groupedConversationTags: Array<Array<number>> | null;
    customerChannel: CustomerChannel | null;
    onlyChatsInGroups?: boolean;
    chatsId?: Array<number>;
    statuses?: Array<ConversationStatus>;
};

export function filterChatByStatus(chat: Conversation, filters: ChatFilters): boolean {
    if (filters.status === "ACTIVE") {
        return chat.status === "ACTIVE";
    } else if (filters.status === "ENDED") {
        return chat.status === "ENDED";
    } else if (filters.status === "INIT_BY_AGENT") {
        return chat.status === "INIT_BY_AGENT";
    }
    return true;
}

export function filterChatByWhichAreWaitingAnswer(
    chat: Conversation,
    filters: ChatFilters
): boolean {
    if (filters.isWaiting === true) {
        return chat.startedWaitingOn !== undefined;
    } else if (filters.isWaiting === false) {
        return chat.startedWaitingOn === undefined;
    }
    return true;
}
export function filterChatByChannel(chat: Conversation, filters: ChatFilters): boolean {
    if (filters.customerChannel === CTA.CHANNELS.OPTIWE_CHAT) {
        return chat.customerChannel.type === CTA.CHANNELS.OPTIWE_CHAT;
    } else if (filters.customerChannel === CTA.CHANNELS.WHATSAPP) {
        return chat.customerChannel.type === CTA.CHANNELS.WHATSAPP;
    } else if (filters.customerChannel === CTA.CHANNELS.FACEBOOK) {
        return chat.customerChannel.type === CTA.CHANNELS.FACEBOOK;
    } else if (filters.customerChannel === CTA.CHANNELS.INSTAGRAM) {
        return chat.customerChannel.type === CTA.CHANNELS.INSTAGRAM;
    }
    return true;
}

export function agentHasSameChatGroup(
    groups: Array<ConversationGroup>,
    currentAgentChatGroups: Array<ConversationGroup> | undefined
): boolean {
    if (!currentAgentChatGroups) {
        return false;
    }
    return (
        groups.filter((group) =>
            currentAgentChatGroups.some((agentGroup) => group.id === agentGroup.id)
        ).length > 0
    );
}

export function filterChatByOwner(
    chat: Conversation,
    currentAgent: Agent,
    showFilter?: ConversationOwner | "ALL"
): boolean {
    if (chat.groups.length > 0 && !agentHasSameChatGroup(chat.groups, currentAgent.chatGroups)) {
        return false;
    }
    if (showFilter === "ME") {
        return Boolean(chat.ownedBy && chat.ownedBy.id === currentAgent.id);
    } else if (showFilter === "UNASSIGNED") {
        return !chat.ownedBy;
    } else if (showFilter === "AI_AGENT") {
        return chat.ownedByAgentType!.includes("BOT");
    } else if (showFilter === "ALL") {
        return chat.hasOwnProperty("ownedBy");
    } else {
        return true;
    }
}

function sortBy(
    a: Conversation,
    b: Conversation,
    field: ChatSortableFields,
    orderDirection: "ASC" | "DESC"
): number {
    if (field === "LAST_END_USER_ACTIVITY") {
        let aActivityTime = new Date(a.lastEndUserActivity!).getTime();
        let bActivityTime = new Date(b.lastEndUserActivity!).getTime();
        if (orderDirection) {
            return aActivityTime - bActivityTime;
        } else {
            return bActivityTime - aActivityTime;
        }
    } else if (field === "ASSIGNED_TO_HUMAN_AGENT_ON") {
        let aTime = new Date(a.assignedToHumanAgentOn!).getTime();
        let bTime = new Date(b.assignedToHumanAgentOn!).getTime();
        if (!aTime && !bTime) {
            return 0;
        }
        if (!aTime) {
            return 1;
        }
        if (!bTime) {
            return -1;
        }
        if (orderDirection) {
            return bTime - aTime;
        } else {
            return aTime - bTime;
        }
    } else {
        // Enters here if field === START_WAITING_ON of undefinied or null or
        // Default sorting mode: START_WAITING_ON
        let aWaitingTime = new Date(a.startedWaitingOn!).getTime();
        let bWaitingTime = new Date(b.startedWaitingOn!).getTime();
        if (!aWaitingTime && !bWaitingTime) {
            return 0;
        }
        if (!aWaitingTime) {
            return 1;
        }
        if (!bWaitingTime) {
            return -1;
        }
        if (orderDirection) {
            return bWaitingTime - aWaitingTime;
        } else {
            return aWaitingTime - bWaitingTime;
        }
    }
}

export function sortByConfigTime(
    a: Conversation,
    b: Conversation,
    filters: ChatFilters,
    conversationOrderType: ChatSortableFields
) {
    if (!filters.sortBy) {
        return sortBy(a, b, conversationOrderType, filters.orderDirection!);
    } else {
        return sortBy(a, b, filters.sortBy, filters.orderDirection!);
    }
}

type ChatResponse = AxiosResponse<{
    conversations: Conversation[];
    paginator: { page: number; hasNext: boolean };
}>;

export async function fetchChats(
    filters: ChatFilters,
    useActiveInMe?: boolean,
    chats: Array<Conversation> | undefined = undefined
): Promise<[Conversation[], { page: number; hasNext: boolean }]> {
    const filtersCopy = { ...filters };

    let activeChatsPromise = new Promise((resolve) => resolve({ data: { conversations: [] } }));
    if (filters.owner === "ME" && !useActiveInMe) {
        activeChatsPromise = api.chats().list({
            status: "ACTIVE",
            owner: "ME",
            sortBy: CTA.FILTERS.DATE_FIELD_FILTERS.START_WAITING_ON,
            useSortConfig: false,
            paginate: false,
        });
        filtersCopy.statuses = ["ENDED", "INIT_BY_AGENT"];
    }
    const resChatsPromise = api.chats().list({ ...filtersCopy });
    const [resChats, activeChats] = (await Promise.all([resChatsPromise, activeChatsPromise])) as [
        ChatResponse,
        ChatResponse
    ];

    const allChats = [
        ...activeChats.data.conversations,
        ...resChats.data.conversations,
        ...(chats || []),
    ];
    const paginator = resChats.data.paginator;
    return [removeDuplicates(allChats), paginator];
}

/**
 * Extracts the chat ID from a given URL if it contains the "/chat/{chatId}" pattern. undefined otherwise
 */
export function extractChatIdFromUrl(url: string): number | undefined {
    const regex = /\/chat\/(\d+)/;
    const match = url.match(regex);

    if (match && match[1]) {
        const chatIdString = match[1];
        const chatId = Number(chatIdString);
        if (chatId) return chatId;
    }
    return undefined;
}

export function filterConfigOnlyActive(deepLinkChatId: number | undefined): ChatFilters {
    return {
        status: CTA.FILTERS.ACTIVE as ConversationStatus,
        owner: CTA.FILTERS.ME as ConversationOwner,
        startDatetime: null,
        endDatetime: null,
        dateFieldFilter: null,
        sortBy: null,
        searchText: null,
        isWaiting: null,
        notServed: null,
        useSortConfig: true,
        orderDirection: null,
        filterByUserGroups: null,
        chatGroupsId: null,
        conversationLabels: null,
        participatingAgentsId: null,
        onlyOwners: null,
        groupedConversationTags: null,
        customerChannel: null,
        onlyChatsInGroups: undefined,
        chatsId: deepLinkChatId ? [deepLinkChatId] : undefined,
    };
}

export function filterConfigAll(deepLinkChatId: number | undefined): ChatFilters {
    return {
        status: undefined,
        owner: CTA.FILTERS.ME as ConversationOwner,
        startDatetime: null,
        endDatetime: null,
        dateFieldFilter: null,
        sortBy: CTA.FILTERS.LAST_END_USER_ACTIVITY as ChatSortableFields,
        searchText: null,
        isWaiting: null,
        notServed: null,
        useSortConfig: false,
        orderDirection: null,
        filterByUserGroups: null,
        chatGroupsId: null,
        conversationLabels: null,
        participatingAgentsId: null,
        onlyOwners: null,
        groupedConversationTags: null,
        customerChannel: null,
        onlyChatsInGroups: undefined,
        chatsId: deepLinkChatId ? [deepLinkChatId] : undefined,
        statuses: ["ENDED", "INIT_BY_AGENT"],
    };
}
